import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CheckCircle2, Eraser } from "lucide-react";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FBF7F1";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#1F4032";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FBF7F1";
    ctx.fillRect(0, 0, c.width, c.height);
    onChange(null);
  };

  return (
    <div>
      <div className="border border-[#1F4032]/15 rounded-md overflow-hidden bg-[#FBF7F1]">
        <canvas
          ref={canvasRef}
          width={600} height={200}
          className="w-full h-44 touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="confirm-signature-canvas"
        />
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-muted-foreground">Signez avec la souris ou le doigt</span>
        <button onClick={clear} type="button" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-[#1F4032]">
          <Eraser className="w-3 h-3" /> Effacer
        </button>
      </div>
    </div>
  );
}

export default function ShipperConfirmDialog({ open, onOpenChange, onConfirm, loading, transporterAmount, commission }) {
  const [signature, setSignature] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => { if (!open) { setSignature(null); setName(""); } }, [open]);

  const ready = signature && name.trim().length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="shipper-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer la réception du colis</DialogTitle>
          <p className="text-sm text-muted-foreground">
            En signant, vous attestez avoir reçu la marchandise en bon état. Le paiement
            sera libéré au transporteur ({transporterAmount}€) après votre confirmation
            (commission plateforme {commission}€).
          </p>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="overline">Votre signature</Label>
            <div className="mt-1">
              <SignaturePad onChange={setSignature} />
            </div>
          </div>
          <div>
            <Label className="overline">Votre nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Nom et prénom" className="mt-1 h-11"
              data-testid="confirm-name" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>Plus tard</Button>
          <Button
            disabled={!ready || loading}
            onClick={() => onConfirm({ signature, signed_name: name })}
            className="bg-[#1F4032] hover:bg-[#2A5443] gap-2"
            data-testid="shipper-confirm-submit">
            <CheckCircle2 className="w-4 h-4" />
            {loading ? "Confirmation…" : "Confirmer & libérer le paiement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
